class LumiereBuilder {
  constructor(root) {
    this.root = root;
    this.data = JSON.parse(root.querySelector('[data-builder-data]').textContent);
    this.data.crystals = this.data.crystals || this.data.products || [];
    this.data.spacers = this.data.spacers || [];
    this.size = 8;
    this.wrist = 130;
    this.activeKind = 'crystal';
    this.selectedCrystal = 0;
    this.selectedSpacer = 0;
    this.selectedSpacerVariants = new Map();
    this.slots = [];
    this.designId = `PN-${Date.now().toString(36).toUpperCase()}`;
    this.bind();
    this.renderOptions();
    this.rebuild(true);
  }

  bind() {
    this.root.querySelector('[data-wrist]').addEventListener('change', (event) => {
      this.wrist = Number(event.target.value);
      this.rebuild();
    });

    this.root.querySelectorAll('[data-bead-size]').forEach((button) => {
      button.addEventListener('click', () => {
        this.size = Number(button.dataset.beadSize);
        this.root.querySelectorAll('[data-bead-size]').forEach((item) => item.classList.toggle('is-active', item === button));
        this.removeIncompatibleSpacers();
        this.renderOptions();
        this.rebuild();
      });
    });

    this.root.querySelectorAll('[data-component-tab]').forEach((button) => {
      button.addEventListener('click', () => this.activatePanel(button.dataset.componentTab));
    });

    this.root.querySelector('[data-reset]').addEventListener('click', () => {
      this.slots = [];
      this.designId = `PN-${Date.now().toString(36).toUpperCase()}`;
      this.rebuild(true);
    });
    this.root.querySelector('[data-builder-add]').addEventListener('click', () => this.openCompletion());
    this.root.querySelector('[data-add-component]').addEventListener('click', () => this.addSelectedComponent());
    this.root.querySelector('[data-completion-add]').addEventListener('click', () => this.addToCart());
    this.root.querySelector('[data-view-wrist]').addEventListener('click', () => this.openWristPreview());
    this.root.querySelectorAll('[data-size-guide-button]').forEach((button) => {
      button.addEventListener('click', () => this.showSizeGuide(Number(button.dataset.sizeGuideButton)));
    });
    this.root.querySelectorAll('[data-modal-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDialog(button.closest('dialog')));
    });
    this.root.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeDialog(dialog);
      });
    });
    window.addEventListener('resize', () => this.renderRing());
  }

  activatePanel(kind) {
    this.activeKind = kind;
    this.root.querySelectorAll('[data-component-tab]').forEach((button) => {
      const active = button.dataset.componentTab === kind;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.root.querySelectorAll('[data-component-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.componentPanel !== kind;
    });
    this.setMessage(kind === 'spacer'
      ? 'Select a spacer, then tap a position. The fit estimate adjusts to its actual width.'
      : 'Select a crystal, then tap a position to compose your bracelet.');
  }

  crystalVariant(product) {
    if (!product) return null;
    return product.variants.find((variant) => {
      const match = String(variant.option1 || variant.title).match(/\d+(?:\.\d+)?/);
      return Number(match?.[0]) === this.size;
    }) || null;
  }

  spacerVariant(product, variantId = null) {
    if (!product) return null;
    const selectedId = variantId || this.selectedSpacerVariants.get(product.id);
    const selected = product.variants.find((variant) => String(variant.id) === String(selectedId));
    if (selected) return selected;
    return product.variants.find((variant) => variant.available && variant.price > 0) || product.variants[0] || null;
  }

  variantFor(component) {
    if (!component?.product) return null;
    return component.kind === 'spacer' ? this.spacerVariant(component.product, component.variantId) : this.crystalVariant(component.product);
  }

  compatibleSizes(product) {
    const source = Array.isArray(product?.compatibleSizes) ? product.compatibleSizes : String(product?.compatibleSizes || '').split(',');
    const sizes = source.map((value) => Number(String(value).match(/\d+(?:\.\d+)?/)?.[0])).filter(Boolean);
    return sizes.length ? sizes : [6, 8, 10, 12];
  }

  spacerCompatible(product) {
    return this.compatibleSizes(product).includes(this.size);
  }

  tagNumber(product, prefix, fallback) {
    const tag = (product?.tags || []).find((value) => String(value).toLowerCase().startsWith(prefix));
    const number = Number(String(tag || '').match(/\d+(?:\.\d+)?/)?.[0]);
    return number || fallback;
  }

  variantMeasurements(variant) {
    return (String(variant?.option1 || variant?.title || '').match(/\d+(?:\.\d+)?/g) || [])
      .map((value) => Number(value))
      .filter((value) => value > 0);
  }

  variantMeasurement(variant) {
    return this.variantMeasurements(variant)[0] || 0;
  }

  spacerWidth(product, variant = null) {
    const selectedVariant = variant || this.spacerVariant(product);
    return Number(selectedVariant?.widthMm)
      || this.variantMeasurements(selectedVariant)[0]
      || Number(product?.widthMm)
      || this.tagNumber(product, 'width-', 3);
  }

  spacerHole(product) {
    return Number(product?.holeSizeMm) || this.tagNumber(product, 'hole-', 1);
  }

  spacerLength(product, variant = null) {
    const selectedVariant = variant || this.spacerVariant(product);
    const titleMeasurements = this.variantMeasurements(selectedVariant);
    return Number(selectedVariant?.lengthMm)
      || Number(selectedVariant?.diameterMm)
      || titleMeasurements[1]
      || Number(product?.lengthMm)
      || Number(product?.diameterMm)
      || titleMeasurements[0]
      || this.tagNumber(product, 'length-', this.size);
  }

  spacerMaximum(product) {
    return Math.max(1, Number(product?.maxQuantity) || this.tagNumber(product, 'max-', 12));
  }

  liveSpacerVariants(product) {
    return (product?.variants || []).filter((variant) => variant.available && variant.price > 0);
  }

  componentAvailable(kind, product, variantId = null) {
    if (kind === 'spacer' && !this.spacerCompatible(product)) return false;
    const component = { kind, product, variantId };
    const variant = this.variantFor(component);
    return Boolean(variant?.available && variant.price > 0);
  }

  firstAvailableCrystal() {
    const product = this.data.crystals.find((item) => this.componentAvailable('crystal', item));
    return product ? { kind: 'crystal', product } : null;
  }

  renderOptions() {
    this.ensureValidSelections();
    this.renderProductList('crystal', this.data.crystals, this.root.querySelector('[data-crystal-list]'));
    this.renderProductList('spacer', this.data.spacers, this.root.querySelector('[data-spacer-list]'));
    const empty = this.root.querySelector('[data-spacer-empty]');
    if (empty) empty.hidden = this.data.spacers.length > 0;
  }

  ensureValidSelections() {
    if (!this.componentAvailable('crystal', this.data.crystals[this.selectedCrystal])) {
      const index = this.data.crystals.findIndex((product) => this.componentAvailable('crystal', product));
      this.selectedCrystal = Math.max(0, index);
    }
    this.data.spacers.forEach((product) => {
      const selected = this.spacerVariant(product);
      if (!selected?.available || selected.price <= 0) {
        const first = this.liveSpacerVariants(product)[0];
        if (first) this.selectedSpacerVariants.set(product.id, first.id);
      } else if (!this.selectedSpacerVariants.has(product.id)) {
        this.selectedSpacerVariants.set(product.id, selected.id);
      }
    });
    if (!this.componentAvailable('spacer', this.data.spacers[this.selectedSpacer])) {
      const index = this.data.spacers.findIndex((product) => this.componentAvailable('spacer', product));
      this.selectedSpacer = Math.max(0, index);
    }
  }

  renderProductList(kind, products, list) {
    if (!list) return;
    list.innerHTML = '';
    const selectedIndex = kind === 'spacer' ? this.selectedSpacer : this.selectedCrystal;

    products.forEach((product, index) => {
      const enabled = kind === 'spacer'
        ? this.spacerCompatible(product) && this.liveSpacerVariants(product).length > 0
        : this.componentAvailable(kind, product);
      const variant = kind === 'spacer' ? this.spacerVariant(product) : this.crystalVariant(product);
      const card = document.createElement('div');
      card.className = `crystal-option component-option component-option--${kind}${index === selectedIndex ? ' is-active' : ''}${enabled ? '' : ' is-unavailable'}`;

      const selectButton = document.createElement('button');
      selectButton.type = 'button';
      selectButton.className = 'crystal-select';
      selectButton.disabled = !enabled;
      selectButton.setAttribute('aria-label', `Select ${product.title}`);

      const swatchFrame = document.createElement('span');
      swatchFrame.className = 'crystal-swatch-frame';
      if (product.image) {
        const swatch = document.createElement('img');
        swatch.className = 'crystal-swatch';
        swatch.src = product.image;
        swatch.alt = '';
        swatchFrame.appendChild(swatch);
      } else {
        const swatch = document.createElement('span');
        swatch.className = `crystal-swatch crystal-swatch--empty${kind === 'spacer' ? ' spacer-swatch--empty' : ''}`;
        swatchFrame.appendChild(swatch);
      }
      selectButton.appendChild(swatchFrame);

      const copy = document.createElement('span');
      copy.className = 'crystal-option__copy';
      const title = document.createElement('strong');
      title.textContent = product.title;
      const details = document.createElement('small');
      if (kind === 'spacer') {
        const dimensions = product.variants.map((item) => `${this.spacerWidth(product, item)} × ${this.spacerLength(product, item)} mm`);
        const uniqueDimensions = [...new Set(dimensions)];
        details.textContent = `${product.materialType || 'Spacer'} · ${uniqueDimensions.join(' / ')} (width × length) · fits ${this.compatibleSizes(product).join('/')} mm beads`;
      } else {
        details.textContent = product.meaning || product.materialType || '';
      }
      const price = document.createElement('span');
      price.className = 'crystal-option__price';
      price.textContent = enabled ? this.money(variant.price) : (kind === 'spacer' && !this.spacerCompatible(product) ? `Not for ${this.size} mm` : 'Unavailable');
      copy.append(title, details, price);
      selectButton.appendChild(copy);
      selectButton.addEventListener('click', () => {
        if (kind === 'spacer') this.selectedSpacer = index;
        else this.selectedCrystal = index;
        this.renderProductList(kind, products, list);
        const selectedVariant = this.spacerVariant(product);
        const sizeCopy = kind === 'spacer' ? ` · ${this.spacerWidth(product, selectedVariant)} × ${this.spacerLength(product, selectedVariant)} mm` : '';
        this.setMessage(`Selected ${product.title}${sizeCopy}. Tap a position in the bracelet to apply it.`);
      });

      let variantOptions = null;
      if (kind === 'spacer') {
        variantOptions = document.createElement('div');
        variantOptions.className = 'spacer-variant-options';
        variantOptions.setAttribute('aria-label', `${product.title} spacer size`);
        product.variants.forEach((spacerOption) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.textContent = spacerOption.option1 || spacerOption.title;
          optionButton.disabled = !spacerOption.available || spacerOption.price <= 0 || !this.spacerCompatible(product);
          optionButton.classList.toggle('is-active', String(spacerOption.id) === String(variant?.id));
          optionButton.addEventListener('click', () => {
            this.selectedSpacer = index;
            this.selectedSpacerVariants.set(product.id, spacerOption.id);
            this.renderProductList(kind, products, list);
            this.setMessage(`Selected ${product.title} · ${spacerOption.option1 || spacerOption.title}. Tap a position in the bracelet to apply it.`);
          });
          variantOptions.appendChild(optionButton);
        });
      }

      const zoomButton = document.createElement('button');
      zoomButton.type = 'button';
      zoomButton.className = 'crystal-zoom';
      zoomButton.setAttribute('aria-label', `View ${product.title} close-up`);
      zoomButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"></circle><path d="m16 16 4 4"></path><path d="M11 8v6M8 11h6"></path></svg>';
      zoomButton.addEventListener('click', () => this.openComponentZoom(kind, product, variant));

      card.append(selectButton);
      if (variantOptions) card.appendChild(variantOptions);
      card.appendChild(zoomButton);
      list.appendChild(card);
    });
  }

  selectedComponent() {
    const index = this.activeKind === 'spacer' ? this.selectedSpacer : this.selectedCrystal;
    const product = this.activeKind === 'spacer' ? this.data.spacers[index] : this.data.crystals[index];
    if (!product) return null;
    if (this.activeKind === 'spacer') {
      const variant = this.spacerVariant(product);
      return { kind: 'spacer', product, variantId: variant?.id, widthMm: this.spacerWidth(product, variant), variantTitle: variant?.option1 || variant?.title };
    }
    return { kind: 'crystal', product };
  }

  componentWidth(component) {
    return component?.kind === 'spacer'
      ? Math.max(1, Number(component.widthMm) || this.spacerWidth(component.product, this.variantFor(component)))
      : this.size;
  }

  designLength() {
    return this.slots.reduce((sum, component) => sum + this.componentWidth(component), 0);
  }

  fitState() {
    const targetMm = this.wrist;
    const actualMm = this.designLength();
    const differenceMm = actualMm - targetMm;
    const hasSpacer = this.slots.some((component) => component?.kind === 'spacer');
    const maximumAllowanceMm = hasSpacer ? 4 : 2;
    const tooShort = differenceMm < 0;
    const tooLong = differenceMm > maximumAllowanceMm;
    const ready = !tooShort && !tooLong;
    return { targetMm, actualMm, differenceMm, maximumAllowanceMm, hasSpacer, tooShort, tooLong, ready };
  }

  removeIncompatibleSpacers() {
    const fallback = this.firstAvailableCrystal();
    let removed = 0;
    this.slots = this.slots.map((component) => {
      if (component.kind === 'spacer' && !this.spacerCompatible(component.product)) {
        removed += 1;
        return fallback;
      }
      return component;
    }).filter(Boolean);
    if (removed) this.setMessage(`${removed} incompatible spacer${removed > 1 ? 's were' : ' was'} replaced for the ${this.size} mm design.`);
  }

  rebuild(reset = false) {
    const fallback = this.firstAvailableCrystal();
    if (reset) this.slots = [];
    const spacers = this.slots.filter((component) => component?.kind === 'spacer');
    const spacerWidth = spacers.reduce((sum, component) => sum + this.componentWidth(component), 0);
    const desiredCrystals = Math.max(1, Math.ceil(Math.max(this.size, this.wrist - spacerWidth) / this.size));
    let currentCrystals = this.slots.filter((component) => component?.kind === 'crystal').length;

    if (!this.slots.length && fallback) {
      this.slots = Array.from({ length: desiredCrystals }, () => ({ ...fallback }));
      currentCrystals = desiredCrystals;
    }
    while (currentCrystals < desiredCrystals && fallback) {
      this.slots.push({ ...fallback });
      currentCrystals += 1;
    }
    while (currentCrystals > desiredCrystals) {
      const index = this.slots.map((component) => component?.kind).lastIndexOf('crystal');
      if (index < 0) break;
      this.slots.splice(index, 1);
      currentCrystals -= 1;
    }
    this.renderRing();
    this.updateSummary();
  }

  replacePosition(index) {
    const choice = this.selectedComponent();
    if (!choice || !this.componentAvailable(choice.kind, choice.product, choice.variantId)) return;

    if (choice.kind === 'spacer') {
      const currentIsSame = this.slots[index]?.kind === 'spacer'
        && this.slots[index]?.product?.id === choice.product.id
        && String(this.slots[index]?.variantId) === String(choice.variantId);
      const count = this.slots.filter((component) => component?.kind === 'spacer' && component.product.id === choice.product.id).length;
      const max = this.spacerMaximum(choice.product);
      if (!currentIsSame && count >= max) {
        this.setMessage(`You can use up to ${max} of ${choice.product.title} in one bracelet.`);
        return;
      }
    }

    this.slots[index] = { ...choice };
    this.rebuild();
  }

  addSelectedComponent() {
    const choice = this.selectedComponent();
    if (!choice || !this.componentAvailable(choice.kind, choice.product, choice.variantId)) return;

    if (choice.kind === 'spacer') {
      const count = this.slots.filter((component) => component?.kind === 'spacer' && component.product.id === choice.product.id).length;
      const max = this.spacerMaximum(choice.product);
      if (count >= max) {
        this.setMessage(`You can use up to ${max} of ${choice.product.title} in one bracelet.`);
        return;
      }
    }

    this.slots.push({ ...choice });
    this.renderRing();
    this.updateSummary();
    this.setMessage(`${choice.product.title} added. Use the × button on any component to remove it.`);
  }

  removePosition(index) {
    const removed = this.slots[index];
    if (!removed) return;
    this.slots.splice(index, 1);
    this.renderRing();
    this.updateSummary();
    this.setMessage(`${removed.product.title} removed. Add or replace components until the fit status is ready.`);
  }

  renderRing() {
    const ring = this.root.querySelector('[data-bracelet-ring]');
    ring.innerHTML = '';
    if (!this.slots.length) return;

    const totalWidthMm = this.slots.reduce((sum, component) => sum + this.componentWidth(component), 0);
    const maxCrossSectionMm = this.slots.reduce((largest, component) => component.kind === 'spacer'
      ? Math.max(largest, this.spacerLength(component.product, this.variantFor(component)))
      : Math.max(largest, this.size), this.size);
    const maxOuterRadius = Math.max(126, Math.min(245, ring.clientWidth * 0.455));
    const scale = maxOuterRadius / ((totalWidthMm / (Math.PI * 2)) + (maxCrossSectionMm / 2));
    const beadPx = Math.round(Math.max(38, Math.min(104, this.size * scale)));
    const radius = Math.max(96, (totalWidthMm * (beadPx / this.size)) / (Math.PI * 2));
    let travelled = 0;

    this.slots.forEach((component, index) => {
      const widthMm = this.componentWidth(component);
      const angle = (((travelled + widthMm / 2) / totalWidthMm) * 360) - 90;
      travelled += widthMm;
      const pixelsPerMm = beadPx / this.size;
      const componentWidthPx = component.kind === 'spacer'
        ? Math.max(3, widthMm * pixelsPerMm)
        : beadPx;
      const componentHeightPx = component.kind === 'spacer'
        ? Math.max(3, this.spacerLength(component.product, this.variantFor(component)) * pixelsPerMm)
        : beadPx;
      const item = document.createElement('div');
      item.className = `builder-bead builder-bead--${component.kind}`;
      const componentRotation = component.kind === 'spacer' ? 0 : -angle;
      const controlRotation = -(angle + componentRotation);
      item.style.cssText = `--bead-px:${beadPx}px;--component-width:${componentWidthPx}px;--component-height:${componentHeightPx}px;--control-rotation:${controlRotation}deg;transform:rotate(${angle}deg) translateY(-${radius}px) rotate(${componentRotation}deg)`;

      const surface = document.createElement('button');
      surface.type = 'button';
      surface.className = 'builder-bead__surface';
      surface.setAttribute('aria-label', `Replace position ${index + 1}: ${component.product?.title || 'empty'}`);
      if (component.kind === 'spacer') {
        surface.title = `${component.product.title} · ${widthMm} × ${this.spacerLength(component.product, this.variantFor(component))} mm (width × length)`;
      }

      if (component.product?.image) {
        const image = document.createElement('img');
        image.src = component.product.image;
        image.alt = '';
        surface.appendChild(image);
      } else if (component.kind === 'spacer') {
        const placeholder = document.createElement('span');
        placeholder.className = 'builder-spacer-placeholder';
        surface.appendChild(placeholder);
      }
      surface.addEventListener('click', () => this.replacePosition(index));

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'builder-bead__remove';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${component.product?.title || `component ${index + 1}`}`);
      remove.addEventListener('click', () => this.removePosition(index));

      item.append(surface, remove);
      ring.appendChild(item);
    });

    this.root.querySelector('[data-fit-label]').textContent = `${this.wrist / 10} cm · ${this.size} mm`;
  }

  openComponentZoom(kind, product, selectedVariant = null) {
    const dialog = this.root.querySelector('[data-crystal-modal]');
    const image = dialog.querySelector('[data-zoom-image]');
    image.src = product.zoomImage || product.image || '';
    image.alt = product.title;
    dialog.querySelector('[data-zoom-kicker]').textContent = kind === 'spacer' ? 'Spacer detail' : 'Crystal close-up';
    dialog.querySelector('[data-zoom-title]').textContent = product.title;
    dialog.querySelector('[data-zoom-meaning]').textContent = kind === 'spacer'
      ? `${product.materialType || 'Spacer component'} · ${this.spacerWidth(product, selectedVariant)} × ${this.spacerLength(product, selectedVariant)} mm (width × length) · ${this.spacerHole(product)} mm hole`
      : (product.meaning || product.materialType || '');
    dialog.querySelector('[data-zoom-note]').textContent = kind === 'spacer'
      ? 'Finish and proportions can vary slightly by production batch. Confirm every component measurement before publishing.'
      : 'Natural colour, texture and inclusions can vary from bead to bead.';
    this.openDialog(dialog);
  }

  openWristPreview() {
    const dialog = this.root.querySelector('[data-wrist-modal]');
    this.showSizeGuide(this.size);
    this.openDialog(dialog);
  }

  showSizeGuide(size) {
    const button = this.root.querySelector(`[data-size-guide-button="${size}"]`);
    if (!button) return;
    const descriptions = {
      6: 'A delicate, understated profile for subtle everyday styling.',
      8: 'A balanced everyday scale with a noticeable crystal presence.',
      10: 'A bolder profile that brings the crystal texture forward.',
      12: 'A statement scale with the strongest visual presence.'
    };
    const image = this.root.querySelector('[data-size-guide-image]');
    image.src = button.dataset.guideSrc;
    image.alt = `${size} mm clear quartz bracelet size reference`;
    this.root.querySelector('[data-size-guide-title]').textContent = `${size} mm`;
    this.root.querySelector('[data-size-guide-copy]').textContent = descriptions[size];
    this.root.querySelectorAll('[data-size-guide-button]').forEach((item) => {
      item.classList.toggle('is-active', item === button);
      item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
    });
  }

  openDialog(dialog) {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  closeDialog(dialog) {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  openCompletion() {
    const fit = this.fitState();
    if (!this.canComplete || !fit.ready) return;
    const dialog = this.root.querySelector('[data-completion-modal]');
    dialog.querySelector('[data-completion-wrist]').textContent = `${this.wrist / 10} cm`;
    dialog.querySelector('[data-completion-length]').textContent = `${(fit.actualMm / 10).toFixed(1)} cm`;
    this.openDialog(dialog);
  }

  setMessage(message) {
    this.root.querySelector('[data-builder-message]').textContent = message;
  }

  updateSummary() {
    const crystalCount = this.slots.filter((component) => component?.kind === 'crystal').length;
    const spacerCount = this.slots.filter((component) => component?.kind === 'spacer').length;
    const spacerWidthTotal = this.slots
      .filter((component) => component?.kind === 'spacer')
      .reduce((sum, component) => sum + this.componentWidth(component), 0);
    const componentTotal = this.slots.reduce((sum, component) => sum + (this.variantFor(component)?.price || 0), 0);
    const componentsValid = this.slots.length > 0 && this.slots.every((component) => {
      const variant = this.variantFor(component);
      return variant?.available && variant.price > 0;
    });
    const fit = this.fitState();
    const valid = Boolean(fit.ready && componentsValid && this.data.baseVariantId && this.data.basePrice > 0);
    this.canComplete = valid;

    this.root.querySelector('[data-slot-count]').textContent = spacerCount
      ? `${crystalCount} crystals · ${spacerCount} spacers`
      : `${crystalCount} crystal beads`;
    this.root.querySelector('[data-summary-wrist]').textContent = `${this.wrist / 10} cm`;
    this.root.querySelector('[data-summary-size]').textContent = `${this.size} mm`;
    this.root.querySelector('[data-summary-crystals]').textContent = crystalCount;
    this.root.querySelector('[data-summary-spacers]').textContent = spacerCount;
    this.root.querySelector('[data-summary-spacer-width]').textContent = `${spacerWidthTotal.toFixed(1)} mm`;
    this.root.querySelector('[data-summary-target]').textContent = `${(fit.targetMm / 10).toFixed(1)} cm`;
    this.root.querySelector('[data-summary-length]').textContent = `${(fit.actualMm / 10).toFixed(1)} cm`;
    const fitStatus = this.root.querySelector('[data-fit-status]');
    fitStatus.classList.toggle('is-ready', fit.ready);
    fitStatus.classList.toggle('needs-adjustment', !fit.ready);
    if (fit.ready) {
      fitStatus.textContent = fit.differenceMm === 0 ? 'Perfect fit' : `Fit ready · +${fit.differenceMm.toFixed(0)} mm allowance`;
    } else if (fit.tooShort) {
      fitStatus.textContent = `Add at least ${Math.abs(fit.differenceMm).toFixed(0)} mm`;
    } else {
      fitStatus.textContent = `Size exceeded · remove at least ${(fit.differenceMm - fit.maximumAllowanceMm).toFixed(0)} mm`;
      this.setMessage(`This design is ${fit.differenceMm.toFixed(0)} mm over the selected wrist size. The maximum allowance is ${fit.maximumAllowanceMm} mm${fit.hasSpacer ? ' when spacers are used' : ''}. Remove or replace a component to continue.`);
    }
    this.root.querySelector('[data-base-price]').textContent = this.data.basePrice ? this.money(this.data.basePrice) : 'Set in Shopify';
    this.root.querySelector('[data-total]').textContent = valid ? this.money(this.data.basePrice + componentTotal) : 'Complete setup';
    this.root.querySelector('[data-builder-add]').disabled = !valid;
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.data.currency
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${this.data.currency}`;
    }
  }

  async addToCart() {
    const button = this.root.querySelector('[data-completion-add]');
    button.disabled = true;
    button.textContent = 'Adding…';
    const counts = new Map();
    const sequenceCodes = [];
    const variantCodes = new Map();
    const sequence = this.slots.map((component) => component.kind === 'spacer'
      ? `Spacer: ${component.product.title} (${component.variantTitle || `${this.componentWidth(component)} mm`})`
      : `Crystal: ${component.product.title}`).join(' · ');
    const crystalCount = this.slots.filter((component) => component.kind === 'crystal').length;
    const spacerCount = this.slots.length - crystalCount;
    const spacerWidthTotal = this.slots
      .filter((component) => component.kind === 'spacer')
      .reduce((sum, component) => sum + this.componentWidth(component), 0);
    const fit = this.fitState();

    this.slots.forEach((component) => {
      const variant = this.variantFor(component);
      const key = `${component.kind}:${variant.id}`;
      if (!variantCodes.has(key)) variantCodes.set(key, variantCodes.size.toString(36));
      const code = variantCodes.get(key);
      sequenceCodes.push(code);
      const current = counts.get(key) || { id: variant.id, quantity: 0, kind: component.kind, code };
      current.quantity += 1;
      counts.set(key, current);
    });

    const items = [{
      id: this.data.baseVariantId,
      quantity: 1,
      properties: {
        'Design ID': this.designId,
        'Wrist circumference': `${this.wrist / 10} cm`,
        'Design length': `${(fit.actualMm / 10).toFixed(1)} cm`,
        'Fit status': fit.differenceMm === 0 ? 'Exact target' : `Ready (+${fit.differenceMm.toFixed(0)} mm allowance)`,
        'Bead diameter': `${this.size} mm`,
        'Crystal beads': crystalCount,
        'Spacer components': spacerCount,
        'Spacer total width': `${spacerWidthTotal.toFixed(1)} mm`,
        'Component sequence': sequence,
        '_la_sequence': sequenceCodes.join('.')
      }
    }];
    counts.forEach((entry) => {
      items.push({
        id: entry.id,
        quantity: entry.quantity,
        properties: {
          _la_design_id: this.designId,
          _la_component: entry.kind === 'spacer' ? 'Spacer' : 'Crystal bead',
          _la_code: entry.code
        }
      });
    });

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('Cart request failed');
      window.location.href = `${shopRoot}cart`;
    } catch (error) {
      this.setMessage('We could not add this design. Please check component prices and inventory, then try again.');
      button.disabled = false;
      button.textContent = 'Add to bag';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-builder-section]').forEach((element) => new LumiereBuilder(element));
});
